// Profilo tecnico con agenda, KPI, certificazioni e stock furgone
import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Package, AlertTriangle, CheckCircle, Clock, ChevronRight } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { Avatar } from '../../components/ui/Avatar'
import { Badge } from '../../components/ui/Badge'
import { Tabs, TabsContent } from '../../components/ui/Tabs'
import { Alert } from '../../components/ui/Alert'
import { Skeleton } from '../../components/ui/Skeleton'
import { Table } from '../../components/ui/Table'
import { formatDate } from '../../lib/utils'
import api from '../../lib/api'

interface TechnicianDetail {
  id: string
  firstName?: string
  lastName?: string
  email: string
  phone?: string
  role: string
  isActive?: boolean
  branchId?: string | null
}

interface Branch {
  id: string
  name: string
  city?: string
}

interface Ticket {
  id: string
  ticketNumber?: string
  title: string
  status: string
  priority?: string
  createdAt: string
  dueDate?: string
}

interface Certification {
  id: string
  type: string
  certificateNumber?: string
  issuer?: string
  issueDate?: string
  expiryDate?: string
  isVerified?: boolean
  status?: string
}

interface VanStockItem {
  id: string
  materialId: string
  materialCode: string
  materialName: string
  materialUnit: string
  materialCategory?: string
  quantity: number
  minQuantity: number
}

function getTechName(t: TechnicianDetail): string {
  if (t.firstName || t.lastName) return `${t.firstName ?? ''} ${t.lastName ?? ''}`.trim()
  return t.email
}

const TICKET_STATUS_VARIANT: Record<string, 'success' | 'danger' | 'warning' | 'default' | 'info'> = {
  open: 'info',
  assigned: 'warning',
  in_progress: 'warning',
  completed: 'success',
  closed: 'default',
  cancelled: 'danger',
}
const TICKET_STATUS_LABEL: Record<string, string> = {
  open: 'Aperto',
  assigned: 'Assegnato',
  in_progress: 'In corso',
  completed: 'Completato',
  closed: 'Chiuso',
  cancelled: 'Annullato',
}
const CERT_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
  valid: 'success',
  expiring: 'warning',
  expired: 'danger',
}
const CERT_STATUS_LABEL: Record<string, string> = {
  valid: 'Valido',
  expiring: 'In scadenza',
  expired: 'Scaduto',
}

const TABS = [
  { value: 'overview', label: 'Profilo' },
  { value: 'agenda', label: 'Agenda' },
  { value: 'certifications', label: 'Abilitazioni' },
  { value: 'vanstock', label: 'Stock furgone' },
]

export function TechnicianDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [tab, setTab] = useState('overview')
  const qc = useQueryClient()

  const { data: tech, isLoading, error } = useQuery({
    queryKey: ['technician', id],
    queryFn: async () => {
      const res = await api.get<TechnicianDetail>(`/technicians/${id}`)
      return res.data
    },
    enabled: Boolean(id),
  })

  // Ticket assegnati — caricati solo quando il tab è attivo
  const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: ['technician-tickets', id],
    queryFn: async () => {
      const res = await api.get<{ items: Ticket[]; total: number }>(
        `/tickets?assignedTo=${id}&pageSize=50`,
      )
      return res.data?.items ?? []
    },
    enabled: Boolean(id) && tab === 'agenda',
  })

  // Certificazioni
  const { data: certifications = [], isLoading: certsLoading } = useQuery({
    queryKey: ['technician-certs', id],
    queryFn: async () => {
      const res = await api.get<Certification[]>(`/hse/certifications?technicianId=${id}`)
      return res.data ?? []
    },
    enabled: Boolean(id) && tab === 'certifications',
  })

  // Van stock
  const { data: vanStock, isLoading: stockLoading } = useQuery({
    queryKey: ['van-stock', id],
    queryFn: async () => {
      try {
        const res = await api.get<any>(`/warehouse/van/${id}`)
        const raw = res.data
        const items: any[] = Array.isArray(raw?.items) ? raw.items : []
        return items.map((item: any): VanStockItem => ({
          id: item.id,
          materialId: item.materialId,
          materialCode: item.material?.code ?? '',
          materialName: item.material?.name ?? '',
          materialUnit: item.material?.unit ?? 'pz',
          materialCategory: item.material?.category ?? null,
          quantity: Number(item.quantity),
          minQuantity: Number(item.minQuantity),
        }))
      } catch {
        return [] as VanStockItem[]
      }
    },
    enabled: Boolean(id) && tab === 'vanstock',
  })

  // Filiali disponibili
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => {
      const res = await api.get<Branch[]>('/companies/branches')
      return res.data ?? []
    },
  })

  const branchMutation = useMutation({
    mutationFn: (branchId: string | null) =>
      api.patch(`/users/${id}`, { branchId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['technician', id] }),
  })

  if (error) return <Alert variant="error">Impossibile caricare il profilo tecnico.</Alert>
  if (isLoading) return <Skeleton height={400} className="rounded-lg" />
  if (!tech) return <Alert variant="error">Tecnico non trovato.</Alert>

  const displayName = getTechName(tech)
  const tickets = ticketsData ?? []
  const openTickets = tickets.filter((t) => ['open', 'assigned', 'in_progress'].includes(t.status)).length
  const completedTickets = tickets.filter((t) => t.status === 'completed').length

  const ticketColumns = [
    {
      key: 'number',
      header: 'N°',
      cell: (t: Ticket) => (
        <span className="font-mono text-xs text-[var(--fc-foreground-muted)]">
          {t.ticketNumber ?? t.id.slice(0, 8)}
        </span>
      ),
      className: 'w-24',
    },
    {
      key: 'title',
      header: 'Titolo',
      cell: (t: Ticket) => (
        <Link
          to={`/tickets/${t.id}`}
          className="text-sm font-medium text-[var(--fc-primary)] hover:underline flex items-center gap-1"
        >
          {t.title}
          <ExternalLink className="h-3 w-3 opacity-50" />
        </Link>
      ),
    },
    {
      key: 'status',
      header: 'Stato',
      cell: (t: Ticket) => (
        <Badge variant={TICKET_STATUS_VARIANT[t.status] ?? 'default'}>
          {TICKET_STATUS_LABEL[t.status] ?? t.status}
        </Badge>
      ),
      className: 'w-32',
    },
    {
      key: 'date',
      header: 'Creato',
      cell: (t: Ticket) => (
        <span className="text-xs text-[var(--fc-foreground-muted)]">
          {formatDate(t.createdAt, 'dd/MM/yyyy')}
        </span>
      ),
      className: 'w-28',
    },
    {
      key: 'due',
      header: 'Scadenza',
      cell: (t: Ticket) => (
        <span className="text-xs text-[var(--fc-foreground-muted)]">
          {t.dueDate ? formatDate(t.dueDate, 'dd/MM/yyyy') : '—'}
        </span>
      ),
      className: 'w-28',
    },
  ]

  const certColumns = [
    {
      key: 'type',
      header: 'Tipo',
      cell: (c: Certification) => (
        <span className="text-sm font-medium text-[var(--fc-foreground)]">{c.type}</span>
      ),
    },
    {
      key: 'number',
      header: 'N° certificato',
      cell: (c: Certification) => (
        <span className="font-mono text-xs text-[var(--fc-foreground-muted)]">
          {c.certificateNumber ?? '—'}
        </span>
      ),
      className: 'w-36',
    },
    {
      key: 'issuer',
      header: 'Ente',
      cell: (c: Certification) => (
        <span className="text-sm text-[var(--fc-foreground-muted)]">{c.issuer ?? '—'}</span>
      ),
    },
    {
      key: 'expiry',
      header: 'Scadenza',
      cell: (c: Certification) => (
        <span className="text-sm">{c.expiryDate ? formatDate(c.expiryDate, 'dd/MM/yyyy') : '—'}</span>
      ),
      className: 'w-28',
    },
    {
      key: 'status',
      header: 'Stato',
      cell: (c: Certification) => {
        const s = c.status ?? 'valid'
        return (
          <div className="flex items-center gap-2">
            <Badge variant={CERT_STATUS_VARIANT[s] ?? 'default'}>
              {CERT_STATUS_LABEL[s] ?? s}
            </Badge>
            {c.isVerified && (
              <CheckCircle className="h-3.5 w-3.5 text-[var(--fc-success)]" aria-label="Verificato" />
            )}
          </div>
        )
      },
      className: 'w-36',
    },
  ]

  const stockColumns = [
    {
      key: 'code',
      header: 'Codice',
      cell: (s: VanStockItem) => (
        <span className="font-mono text-xs text-[var(--fc-foreground-muted)]">{s.materialCode}</span>
      ),
      className: 'w-28',
    },
    {
      key: 'name',
      header: 'Materiale',
      cell: (s: VanStockItem) => (
        <span className="text-sm font-medium text-[var(--fc-foreground)]">{s.materialName}</span>
      ),
    },
    {
      key: 'category',
      header: 'Categoria',
      cell: (s: VanStockItem) => (
        <span className="text-sm text-[var(--fc-foreground-muted)]">{s.materialCategory ?? '—'}</span>
      ),
    },
    {
      key: 'qty',
      header: 'Q.tà',
      cell: (s: VanStockItem) => {
        const low = s.quantity <= s.minQuantity && s.minQuantity > 0
        return (
          <div className="flex items-center gap-1.5">
            <span className={`font-semibold ${low ? 'text-[var(--fc-danger)]' : 'text-[var(--fc-success)]'}`}>
              {s.quantity}
            </span>
            <span className="text-xs text-[var(--fc-foreground-muted)]">{s.materialUnit}</span>
            {low && <AlertTriangle className="h-3.5 w-3.5 text-[var(--fc-danger)]" aria-label="Sotto scorta" />}
          </div>
        )
      },
      className: 'w-28',
    },
    {
      key: 'min',
      header: 'Min',
      cell: (s: VanStockItem) => (
        <span className="text-xs text-[var(--fc-foreground-muted)]">{s.minQuantity}</span>
      ),
      className: 'w-16',
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title={displayName}
        breadcrumb={[{ label: 'Tecnici', href: '/technicians' }, { label: displayName }]}
      />

      {/* Header profilo */}
      <Card>
        <div className="flex items-center gap-4">
          <Avatar name={displayName} size="xl" online={tech.isActive ?? false} />
          <div>
            <h2 className="text-xl font-bold text-[var(--fc-foreground)]">{displayName}</h2>
            <p className="text-sm text-[var(--fc-foreground-muted)]">{tech.email}</p>
            <div className="mt-2 flex gap-2">
              <Badge variant={tech.isActive ? 'success' : 'default'} dot>
                {tech.isActive ? 'Attivo' : 'Non attivo'}
              </Badge>
              <Badge variant="info">{tech.role}</Badge>
            </div>
          </div>
        </div>

        {/* KPI rapidi — calcolati dai ticket reali dopo caricamento */}
        <div className="mt-4 grid grid-cols-3 gap-4 border-t border-[var(--fc-border)] pt-4">
          {[
            { label: 'Ticket aperti', value: tab === 'agenda' ? openTickets : '—', icon: <Clock className="h-4 w-4" /> },
            { label: 'Completati', value: tab === 'agenda' ? completedTickets : '—', icon: <CheckCircle className="h-4 w-4" /> },
            { label: 'Materiali a bordo', value: tab === 'vanstock' ? (vanStock?.length ?? '—') : '—', icon: <Package className="h-4 w-4" /> },
          ].map(({ label, value, icon }) => (
            <div key={label} className="text-center">
              <div className="flex items-center justify-center gap-1 text-2xl font-bold text-[var(--fc-foreground)]">
                {value}
              </div>
              <p className="text-xs text-[var(--fc-foreground-muted)] mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Tabs */}
      <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] overflow-hidden">
        <Tabs tabs={TABS} value={tab} onValueChange={setTab}>

          {/* ── Profilo ── */}
          <TabsContent value="overview" className="p-5">
            <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div>
                <dt className="text-[var(--fc-foreground-muted)]">Nome</dt>
                <dd className="font-medium text-[var(--fc-foreground)]">{displayName}</dd>
              </div>
              <div>
                <dt className="text-[var(--fc-foreground-muted)]">Email</dt>
                <dd className="font-medium text-[var(--fc-foreground)]">{tech.email}</dd>
              </div>
              {tech.phone && (
                <div>
                  <dt className="text-[var(--fc-foreground-muted)]">Telefono</dt>
                  <dd className="font-medium text-[var(--fc-foreground)]">{tech.phone}</dd>
                </div>
              )}
              <div>
                <dt className="text-[var(--fc-foreground-muted)]">Ruolo</dt>
                <dd className="font-medium text-[var(--fc-foreground)]">{tech.role}</dd>
              </div>
              <div>
                <dt className="text-[var(--fc-foreground-muted)]">Stato account</dt>
                <dd>
                  <Badge variant={tech.isActive ? 'success' : 'default'}>
                    {tech.isActive ? 'Attivo' : 'Disabilitato'}
                  </Badge>
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="text-[var(--fc-foreground-muted)] mb-1">Filiale di appartenenza</dt>
                <dd>
                  <select
                    value={tech.branchId ?? ''}
                    onChange={(e) => branchMutation.mutate(e.target.value || null)}
                    disabled={branchMutation.isPending}
                    className="w-full max-w-xs rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]"
                  >
                    <option value="">— Nessuna filiale —</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}{b.city ? ` (${b.city})` : ''}
                      </option>
                    ))}
                  </select>
                  {branchMutation.isPending && (
                    <span className="ml-2 text-xs text-[var(--fc-foreground-muted)]">Salvataggio...</span>
                  )}
                </dd>
              </div>
            </dl>
          </TabsContent>

          {/* ── Agenda ── */}
          <TabsContent value="agenda" className="p-4">
            {ticketsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} height={40} className="rounded" />)}
              </div>
            ) : (
              <Table
                data={tickets}
                columns={ticketColumns}
                keyExtractor={(t) => t.id}
                emptyMessage="Nessun ticket assegnato"
              />
            )}
          </TabsContent>

          {/* ── Abilitazioni ── */}
          <TabsContent value="certifications" className="p-4">
            {certsLoading ? (
              <div className="space-y-2">
                {[1, 2].map((i) => <Skeleton key={i} height={40} className="rounded" />)}
              </div>
            ) : (
              <Table
                data={certifications}
                columns={certColumns}
                keyExtractor={(c) => c.id}
                emptyMessage="Nessuna abilitazione registrata"
              />
            )}
          </TabsContent>

          {/* ── Stock furgone ── */}
          <TabsContent value="vanstock" className="p-4">
            {stockLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} height={40} className="rounded" />)}
              </div>
            ) : (
              <>
                {(vanStock ?? []).some((s) => s.quantity <= s.minQuantity && s.minQuantity > 0) && (
                  <Alert variant="warning" className="mb-3">
                    Alcuni materiali sono sotto la scorta minima.
                  </Alert>
                )}
                <Table
                  data={vanStock ?? []}
                  columns={stockColumns}
                  keyExtractor={(s) => s.id}
                  emptyMessage="Nessun materiale caricato sul furgone"
                />
              </>
            )}
          </TabsContent>

        </Tabs>
      </div>
    </div>
  )
}
